import { useFetch } from '@/composables/useFetch';
import { useToast } from '@/composables/useToast';
import { endpointsList } from '@/helpers';
import axios from 'axios';
import {
  ICreateWorkspaceGroupDto,
  IWorkspaceGroupSummaryDto,
  IWorkspaceGroupUserDto,
  WorkspaceGroupErrorCode,
} from 'types';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { isCustomErrorCodeException } from '@/helpers';

export function useWorkspaceGroups(workspaceId?: string) {
  const { t } = useI18n();
  const { createErrorToast, createSuccessToast } = useToast();

  const {
    data: workspaceGroups,
    fetchData: fetchWorkspaceGroups,
    isLoading: isLoadingWorkspaceGroups,
  } = useFetch(getWorkspaceGroups, undefined, false);
  async function getWorkspaceGroups() {
    if (!workspaceId) {
      throw new Error('workspaceId is required to fetch workspace groups');
    }
    try {
      const response = await axios.get<IWorkspaceGroupSummaryDto[]>(
        endpointsList.workspaces.getWorkspaceGroups(workspaceId),
      );
      return response.data;
    } catch {
      createErrorToast(
        t('workspaceGroupsManagement.errors.errorGettingGroups'),
      );
    }
  }

  const selectedGroupId = ref<string>();
  const {
    data: workspaceGroupUsers,
    fetchData: fetchWorkspaceGroupUsersInternal,
    isLoading: isLoadingWorkspaceGroupUsers,
  } = useFetch(getWorkspaceGroupUsers, undefined, false);
  async function getWorkspaceGroupUsers() {
    if (!selectedGroupId.value) {
      throw new Error('groupId is required to fetch workspace group users');
    }
    try {
      const response = await axios.get<IWorkspaceGroupUserDto[]>(
        endpointsList.workspaces.getWorkspaceGroupUsers(selectedGroupId.value),
      );
      return response.data;
    } catch {
      createErrorToast(
        t('workspaceGroupsManagement.errors.errorGettingGroupUsers'),
      );
    }
  }

  async function fetchWorkspaceGroupUsers(groupId: string) {
    selectedGroupId.value = groupId;
    await fetchWorkspaceGroupUsersInternal();
  }

  const isCreatingGroup = ref(false);
  async function createWorkspaceGroup(name: string): Promise<boolean> {
    if (!workspaceId) {
      throw new Error('workspaceId is required to create workspace group');
    }
    const dto: ICreateWorkspaceGroupDto = {
      name,
      workspaceId,
    };
    try {
      isCreatingGroup.value = true;
      await axios.post(endpointsList.workspaces.createWorkspaceGroup(), dto);
      createSuccessToast(t('workspaceGroupsManagement.groupCreated'));
      return true;
    } catch {
      createErrorToast(
        t('workspaceGroupsManagement.errors.errorCreatingGroup'),
      );
      return false;
    } finally {
      isCreatingGroup.value = false;
    }
  }

  const isAddingUser = ref(false);
  async function addUserToWorkspaceGroup(groupId: string, userOid: string) {
    try {
      isAddingUser.value = true;
      await axios.post(
        endpointsList.workspaces.addUserToWorkspaceGroup(groupId, userOid),
      );
      createSuccessToast(t('workspaceGroupsManagement.userAdded'));
    } catch (err) {
      if (isCustomErrorCodeException(err)) {
        if (
          err.response?.data.customErrorCode ===
          WorkspaceGroupErrorCode.USER_ALREADY_IN_GROUP
        ) {
          createErrorToast(t('workspaceGroupUsers.errors.userAlreadyInGroup'));
          return;
        }
        if (
          err.response?.data.customErrorCode ===
          WorkspaceGroupErrorCode.USER_ALREADY_IN_ANOTHER_GROUP
        ) {
          createErrorToast(
            t('workspaceGroupUsers.errors.userAlreadyInAnotherGroup'),
          );
          return;
        }
      }
      createErrorToast(t('workspaceGroupsManagement.errors.errorAddingUser'));
    } finally {
      isAddingUser.value = false;
    }
  }

  const isRemovingUser = ref(false);
  async function removeUserFromWorkspaceGroup(
    groupId: string,
    userOid: string,
  ) {
    try {
      isRemovingUser.value = true;
      await axios.delete(
        endpointsList.workspaces.removeUserFromWorkspaceGroup(groupId, userOid),
      );
      createSuccessToast(t('workspaceGroupsManagement.userRemoved'));
    } catch {
      createErrorToast(t('workspaceGroupsManagement.errors.errorRemovingUser'));
    } finally {
      isRemovingUser.value = false;
    }
  }

  return {
    workspaceGroups,
    fetchWorkspaceGroups,
    isLoadingWorkspaceGroups,

    workspaceGroupUsers,
    fetchWorkspaceGroupUsers,
    isLoadingWorkspaceGroupUsers,

    isCreatingGroup,
    createWorkspaceGroup,

    isAddingUser,
    addUserToWorkspaceGroup,

    isRemovingUser,
    removeUserFromWorkspaceGroup,
  };
}
